import { useColorMode, useMediaQuery } from "@chakra-ui/react";
import { GetProgrammaticCampaignActiveAccountEngagementsRO } from "@intentsify/dto";
import { BuyerResearchStage } from "@intentsify/types";
import { useQuery } from "@tanstack/react-query";
import {
  Card,
  MIN_MODAL_CONTENT_HEIGHT,
  StackedGroupedBarChart,
  getChartColors,
} from "components";
import { DisplayPerformanceErrorBoundary } from "../../../../DisplayPerformanceErrorBoundary";
import { DateTime } from "luxon";
import { useMemo, useState } from "react";
import { ChartExport, useExportableChart } from "shared/components";
import { DisplayPerformanceFilters } from "../../../../types";
import { isPopulatedArray } from "@intentsify/utils";
import { MdAnalytics } from "react-icons/md";

type AccountMovementProps = {
  campaignId: string;
  filters: DisplayPerformanceFilters;
  getAccountEngagement: (
    id: string,
    filters: DisplayPerformanceFilters,
  ) => Promise<GetProgrammaticCampaignActiveAccountEngagementsRO>;
};

const TITLE = "Account Movement by Buyer Research Stage";

const AccountMovement = (props: AccountMovementProps) => (
  <DisplayPerformanceErrorBoundary cardTitle={TITLE}>
    <AccountMovementContent {...props} />
  </DisplayPerformanceErrorBoundary>
);

const AccountMovementContent = ({
  campaignId,
  filters,
  getAccountEngagement,
}: AccountMovementProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { colorMode } = useColorMode();
  const [isLargerThan1800] = useMediaQuery("(min-width: 1800px)");

  const { data: engagementData, isFetching: isLoadingEngagementData } =
    useQuery(["getAccountEngagement", campaignId, filters], () =>
      getAccountEngagement(campaignId, filters),
    );

  const dates = useMemo(() => {
    return {
      start: DateTime.fromObject({
        weekNumber: filters.weekFrom,
        weekYear: filters.yearFrom,
      }).toISO(),
      end: DateTime.fromObject({
        weekNumber: filters.weekTo,
        weekYear: filters.yearTo,
      }).toISO(),
    };
  }, [filters]);

  const formattedEngagementData = useMemo(() => {
    if (!engagementData) {
      return [];
    }

    const AllAccounts = engagementData.activeCampaignAccountEngagements.find(
      (i) => i.buyerResearchStage === BuyerResearchStage.All,
    );
    const Decision = engagementData.activeCampaignAccountEngagements.find(
      (i) => i.buyerResearchStage === BuyerResearchStage.Decision,
    );
    const Consideration = engagementData.activeCampaignAccountEngagements.find(
      (i) => i.buyerResearchStage === BuyerResearchStage.Consideration,
    );
    const Interest = engagementData.activeCampaignAccountEngagements.find(
      (i) => i.buyerResearchStage === BuyerResearchStage.Interest,
    );
    const Awareness = engagementData.activeCampaignAccountEngagements.find(
      (i) => i.buyerResearchStage === BuyerResearchStage.Awareness,
    );

    return [
      {
        groupLabel: "Targeted",
        groupItems: [
          {
            label: !isLargerThan1800 ? "All" : "All Accounts",
            data: [
              {
                label: "All Accounts",
                value: AllAccounts?.targetedAllAccounts ?? 0,
              },
            ],
          },
          {
            label: "Trending",
            data: [
              { label: "Decision", value: Decision?.targetedTrending ?? 0 },
              {
                label: "Consideration",
                value: Consideration?.targetedTrending ?? 0,
              },
              { label: "Interest", value: Interest?.targetedTrending ?? 0 },
              { label: "Awareness", value: Awareness?.targetedTrending ?? 0 },
            ],
          },
        ],
      },
      {
        groupLabel: "Reached",
        groupItems: [
          {
            label: !isLargerThan1800 ? "All" : "All Accounts",
            data: [
              {
                label: "All Accounts",
                value: AllAccounts?.reachedAllAccounts ?? 0,
              },
            ],
          },
          {
            label: "Trending",
            data: [
              { label: "Decision", value: Decision?.reachedTrending ?? 0 },
              {
                label: "Consideration",
                value: Consideration?.reachedTrending ?? 0,
              },
              { label: "Interest", value: Interest?.reachedTrending ?? 0 },
              { label: "Awareness", value: Awareness?.reachedTrending ?? 0 },
            ],
          },
        ],
      },
      {
        groupLabel: "Engaged",
        groupItems: [
          {
            label: !isLargerThan1800 ? "All" : "All Accounts",
            data: [
              {
                label: "All Accounts",
                value: AllAccounts?.engagedAllAccounts ?? 0,
              },
            ],
          },
          {
            label: "Trending",
            data: [
              { label: "Decision", value: Decision?.engagedTrending ?? 0 },
              {
                label: "Consideration",
                value: Consideration?.engagedTrending ?? 0,
              },
              { label: "Interest", value: Interest?.engagedTrending ?? 0 },
              { label: "Awareness", value: Awareness?.engagedTrending ?? 0 },
            ],
          },
        ],
      },
    ];
  }, [engagementData, isLargerThan1800]);

  const accountMovement = useExportableChart({
    title: TITLE,
    campaignId: Number(campaignId),
    dates,
    chart: (
      <StackedGroupedBarChart
        minHeight={isExpanded ? MIN_MODAL_CONTENT_HEIGHT : undefined}
        limitedWidth={!isLargerThan1800}
        isLoading={isLoadingEngagementData}
        stretch
        data={formattedEngagementData}
        legendConfig={{
          domain: [
            BuyerResearchStage.Decision,
            BuyerResearchStage.Consideration,
            BuyerResearchStage.Interest,
            BuyerResearchStage.Awareness,
          ],
          range: getChartColors(colorMode, 1).reverse(),
        }}
      />
    ),
  });

  //
  return (
    <Card
      onExpandedChange={setIsExpanded}
      isLoading={isLoadingEngagementData}
      title={accountMovement.title}
      hasData={isPopulatedArray(formattedEngagementData)}
      noDataMessage="Currently there is no data available for the selected filters or visuals."
      noDataIcon={MdAnalytics}
      isExpandable
      actions={
        <ChartExport
          size="small"
          dates={dates}
          campaignId={Number(campaignId)}
          title={accountMovement.title}
          onExportPNG={accountMovement.downloadAsPng}
          data={formattedEngagementData}
          disableCSVExport
        />
      }
    >
      {accountMovement.component}
      {accountMovement.exportComponent}
    </Card>
  );
};

export { AccountMovement };
